class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-viro-continue-shopping]');
      if (!link) return;
      const target = document.getElementById('ViroCompleteSet');
      if (!target) return;
      event.preventDefault();
      this.close();
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    });
    this.addEventListener('click', (event) => {
      const nav = event.target.closest('[data-viro-booster-nav]');
      if (nav && this.contains(nav)) {
        this.moveViroCartBooster(nav.dataset.viroBoosterNav);
        return;
      }
      const add = event.target.closest('[data-viro-booster-add]');
      if (add && this.contains(add)) this.addViroCartBoosterProduct(add);
    });
  }

  connectedCallback() {
    this.viroPageShowHandler = (event) => {
      const navigation = window.performance?.getEntriesByType?.('navigation')[0];
      if (!event.persisted && navigation?.type !== 'back_forward') return;
      this.close();
      this.viroNeedsRefresh = true;
      this.viroHistoryRefresh = this.refreshViroCartAfterHistory();
    };
    window.addEventListener('pageshow', this.viroPageShowHandler);
    this.viroCheckoutHandler = async (event) => {
      if (event.target.id !== 'CartDrawer-Form' || event.submitter?.name !== 'checkout' || !this.viroNeedsRefresh) return;
      event.preventDefault();
      const ready = await this.viroHistoryRefresh;
      if (!ready) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
      const form = this.querySelector('#CartDrawer-Form');
      const button = this.querySelector('#CartDrawer-Checkout');
      if (form && button && !button.disabled) form.requestSubmit(button);
    };
    this.addEventListener('submit', this.viroCheckoutHandler);
    this.viroCartBoosterUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.viroCartRevision = (this.viroCartRevision || 0) + 1;
      if (event.source === 'cart-items') setTimeout(() => this.loadViroCartBooster(), 0);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.viroPageShowHandler);
    this.removeEventListener('submit', this.viroCheckoutHandler);
    if (this.viroCartBoosterUnsubscriber) this.viroCartBoosterUnsubscriber();
  }

  async refreshViroCartAfterHistory() {
    const revision = this.viroCartRevision || 0;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    this.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart refresh failed');
      const sections = await response.json();
      if (!this.isConnected) return false;
      if (revision !== (this.viroCartRevision || 0)) return await this.refreshViroCartAfterHistory();
      const html = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
      const source = html.querySelector('#CartDrawer');
      const sourceDrawer = html.querySelector('cart-drawer');
      if (!source || !sourceDrawer || !sections['cart-icon-bubble']) throw new Error('Cart sections unavailable');
      this.querySelector('#CartDrawer').innerHTML = source.innerHTML;
      this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.viroNeedsRefresh = false;
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timeout);
      this.removeAttribute('aria-busy');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    const request = this.viroOpenRequest = (this.viroOpenRequest || 0) + 1;
    window.viroLoadDeferredStyles?.();
    let timeout;
    const ready = await Promise.race([
      window.viroCartStylesReady,
      new Promise(resolve => { timeout = setTimeout(() => resolve(false), 10000); })
    ]);
    clearTimeout(timeout);
    if (!this.isConnected || request !== this.viroOpenRequest) return;
    if (ready === false) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }
    if (this.viroNeedsRefresh) {
      const refreshed = await this.viroHistoryRefresh;
      if (!this.isConnected || request !== this.viroOpenRequest) return;
      if (!refreshed) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      if (request === this.viroOpenRequest) this.classList.add('animate', 'active');
    });

    const focusDrawer = () => {
      this.removeEventListener('transitionend', focusDrawer);
      clearTimeout(focusTimeout);
      if (request !== this.viroOpenRequest || !this.classList.contains('active')) return;
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container && focusElement) trapFocus(container, focusElement);
    };
    const focusTimeout = setTimeout(focusDrawer, 350);
    this.addEventListener('transitionend', focusDrawer, { once: true });

    document.body.classList.add('overflow-hidden');
    this.loadViroCartBooster();
  }

  moveViroCartBooster(direction) {
    const booster = this.querySelector('[data-viro-cart-booster]');
    const cards = [...(booster?.querySelectorAll('.viro-cart-booster__card') || [])];
    if (!cards.length) return;
    const next = Math.max(0, Math.min(cards.length - 1, Number(booster.dataset.slide || 0) + (direction === 'next' ? 1 : -1)));
    booster.dataset.slide = String(next);
    cards[next].scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest', inline: 'start' });
    this.updateViroCartBoosterControls(booster);
  }

  updateViroCartBoosterControls(booster) {
    const count = booster.querySelectorAll('.viro-cart-booster__card').length;
    const index = Number(booster.dataset.slide || 0);
    const previous = booster.querySelector('[data-viro-booster-nav="previous"]');
    const next = booster.querySelector('[data-viro-booster-nav="next"]');
    if (previous) previous.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
    booster.hidden = count === 0;
  }

  createViroCartBoosterCard(product, booster) {
    const variants = product.variants || [];
    const simpleVariant = variants.length === 1 && variants[0].available && !product.requires_selling_plan ? variants[0] : null;
    const price = Number(simpleVariant?.price || product.price || 0);
    const url = typeof product.url === 'string' && product.url.startsWith('/products/')
      ? product.url
      : `${window.Shopify.routes.root}products/${encodeURIComponent(product.handle)}`;
    const card = document.createElement('article');
    card.className = 'viro-cart-booster__card';
    card.setAttribute('role', 'listitem');
    card.dataset.productId = String(product.id);
    card.dataset.price = String(price);
    const imageLink = document.createElement('a');
    imageLink.className = 'viro-cart-booster__image';
    imageLink.href = url;
    imageLink.setAttribute('aria-label', product.title);
    const imageUrl = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title;
      image.width = 72;
      image.height = 72;
      image.loading = 'lazy';
      imageLink.append(image);
    }
    const details = document.createElement('div');
    details.className = 'viro-cart-booster__details';
    const title = document.createElement('a');
    title.className = 'viro-cart-booster__title';
    title.href = url;
    title.textContent = product.title;
    const priceLabel = document.createElement('span');
    priceLabel.className = 'viro-cart-booster__price';
    priceLabel.textContent = `${new Intl.NumberFormat('en-EG', { maximumFractionDigits: 2 }).format(price / 100)} ${booster.dataset.currencyLabel || 'جنيه مصري'}`;
    details.append(title, priceLabel);
    if (simpleVariant && Number(booster.dataset.subtotal) < Number(booster.dataset.threshold)
      && Number(booster.dataset.subtotal) + price >= Number(booster.dataset.threshold)) {
      const badge = document.createElement('span');
      badge.className = 'viro-cart-booster__badge';
      badge.textContent = 'يوصّلك لخصم 10%';
      details.append(badge);
    }
    const action = document.createElement(simpleVariant ? 'button' : 'a');
    action.className = 'viro-cart-booster__add';
    if (simpleVariant) {
      action.type = 'button';
      action.dataset.viroBoosterAdd = String(simpleVariant.id);
      action.textContent = 'أضف للسلة +';
    } else {
      action.href = url;
      action.textContent = 'اختار المقاس ←';
    }
    details.append(action);
    card.append(imageLink, details);
    return card;
  }

  async loadViroCartBooster() {
    const booster = this.querySelector('[data-viro-cart-booster]');
    if (!booster || booster.dataset.loading || booster.dataset.loaded) return;
    this.updateViroCartBoosterControls(booster);
    if (booster.dataset.curated === 'true' || !booster.dataset.productId) return;
    booster.dataset.loading = 'true';
    try {
      const url = new URL(`${booster.dataset.recommendationsUrl}.json`, window.location.origin);
      url.searchParams.set('product_id', booster.dataset.productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const { products = [] } = await response.json();
      if (!booster.isConnected) return;
      const track = booster.querySelector('[data-viro-booster-track]');
      const cartIds = new Set(booster.dataset.cartProductIds.split(','));
      const seen = new Set(cartIds);
      const relatedCards = [];
      for (const product of products) {
        if (!product.available || !Number(product.price) || seen.has(String(product.id))) continue;
        relatedCards.push(this.createViroCartBoosterCard(product, booster));
        seen.add(String(product.id));
        if (relatedCards.length === 6) break;
      }
      const fallbackCards = [...track.querySelectorAll('.viro-cart-booster__card')]
        .filter((card) => !seen.has(card.dataset.productId))
        .sort((a, b) => Number(b.dataset.price) - Number(a.dataset.price));
      const subtotal = Number(booster.dataset.subtotal);
      const threshold = Number(booster.dataset.threshold);
      const firstFallbackQualifies = fallbackCards.length && subtotal < threshold
        && subtotal + Number(fallbackCards[0].dataset.price) >= threshold;
      const firstRelatedQualifies = relatedCards.length && subtotal < threshold
        && subtotal + Number(relatedCards[0].dataset.price) >= threshold;
      const takeFallbackFirst = firstFallbackQualifies && !firstRelatedQualifies;
      const cards = [];
      while (cards.length < 6 && (relatedCards.length || fallbackCards.length)) {
        if (takeFallbackFirst && fallbackCards.length) cards.push(fallbackCards.shift());
        if (cards.length < 6 && relatedCards.length) cards.push(relatedCards.shift());
        if (cards.length < 6 && !takeFallbackFirst && fallbackCards.length) cards.push(fallbackCards.shift());
      }
      if (cards.length) track.replaceChildren(...cards);
      booster.dataset.slide = '0';
      this.updateViroCartBoosterControls(booster);
    } catch (_error) {
      // Keep the best-selling products that Liquid rendered when recommendations are unavailable.
    } finally {
      if (booster.isConnected) {
        delete booster.dataset.loading;
        booster.dataset.loaded = 'true';
      }
    }
  }

  async addViroCartBoosterProduct(button) {
    if (button.disabled) return;
    button.disabled = true;
    const booster = button.closest('[data-viro-cart-booster]');
    const status = booster?.querySelector('[data-viro-booster-status]');
    if (status) status.textContent = 'بنضيف المنتج للسلة…';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.viroBoosterAdd), quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'تعذّرت إضافة المنتج');
      if (result.sections?.['cart-drawer'] && result.sections?.['cart-icon-bubble']) {
        this.renderContents(result);
      } else {
        window.location.assign(window.routes.cart_url);
      }
    } catch (error) {
      if (status) status.textContent = error.message || 'تعذّرت إضافة المنتج. حاول مرة أخرى.';
      button.disabled = false;
    }
  }

  close() {
    this.viroOpenRequest = (this.viroOpenRequest || 0) + 1;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const html = new DOMParser().parseFromString(parsedState.sections?.['cart-drawer'] || '', 'text/html');
    const sourceDrawer = html.querySelector('cart-drawer');
    const sourceContents = html.querySelector('#CartDrawer');
    const contents = this.querySelector('#CartDrawer');
    if (!sourceDrawer || !sourceContents || !contents) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }
    const bubbleHTML = parsedState.sections?.['cart-icon-bubble'];
    const bubbleContents = bubbleHTML ? this.getSectionDOM(bubbleHTML) : null;
    const bubble = document.getElementById('cart-icon-bubble');

    this.productId = parsedState.id;
    contents.innerHTML = sourceContents.innerHTML;
    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    if (bubble && bubbleContents) bubble.innerHTML = bubbleContents.innerHTML;

    setTimeout(() => {
      if (!this.isConnected) return;
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
